import { ReactNode, useCallback, useEffect, useMemo, useState } from "react"

import { Box, MenuItem, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useFormikContext } from "formik"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { FormikAutoComplete, FormikCheckbox, FormikDropdown } from "../../../../../../shared/components/forms/Formik"
import { LabeledField } from "../../../../../../shared/components/forms/LabeledField"
import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import {
    fetchCompanies,
    fetchUsersForCompany,
    ManageBookingsQuery,
    CompaniesResponse,
    UsersForCompanyResponse,
} from "../manageBookingsApi"
import {
    ManageBookingsAction,
    ManageBookingsFormValues,
    UserOption,
    CustomerOption,
    CompanySelectOption,
} from "../manageBookingsTypes"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

function mapCompaniesToOptions(data: CompaniesResponse): CompanySelectOption[] {
    return data.customers.flatMap((customer: CustomerOption): CompanySelectOption[] => {
        if (customer.isOfficeHotel && customer.userGroups && customer.userGroups.length > 0) {
            return customer.userGroups.map(
                (userGroup): CompanySelectOption => ({
                    type: "tenant",
                    group: customer.name,
                    id: userGroup.id,
                    customerId: customer.id,
                    name: userGroup.name,
                }),
            )
        }
        return [
            {
                type: "customer",
                group: customer.name,
                id: customer.id,
                name: customer.name,
            },
        ]
    })
}

const selectUsers = (data: UsersForCompanyResponse): UserOption[] => data.users

const useStyles = makeStyles()((theme) => ({
    container: {
        display: "flex",
        flexDirection: "column",
    },
    actionDropdown: {
        width: "18rem",
    },
    checkboxSection: {
        marginTop: "1rem",
        display: "flex",
        flexDirection: "column",
    },
    checkboxRow: {
        display: "flex",
        gap: theme.spacing(3),
        alignItems: "flex-start",
        "& > .MuiFormControl-root:first-of-type": {
            minWidth: "11rem",
        },
    },
    companySelect: {
        marginTop: "1rem",
    },
    userSelect: {
        marginTop: "1rem",
    },
    companyOption: {
        marginLeft: "0.5rem",
    },
    alertBox: {
        marginTop: "1rem",
    },
    alertBoxFullWidth: {
        marginBottom: "2rem",
        width: "100%",
    },
    sectionTitle: {
        marginTop: "3rem",
        fontWeight: "bold",
    },
    placeholderText: {
        color: theme.palette.text.primary,
        opacity: 0.42,
    },
}))

const MIN_USER_SEARCH_LENGTH = 3

export function ManageBookingsFormContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { values, setFieldValue } = useFormikContext<ManageBookingsFormValues>()
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const [userSearchInput, setUserSearchInput] = useState("")
    const [newOwnerSearchInput, setNewOwnerSearchInput] = useState("")

    const selectedCompany = values.company
    const showMoveFields = values.action === ManageBookingsAction.CHANGE_ORGANIZER

    const companyId = selectedCompany?.type === "tenant" ? selectedCompany.customerId : selectedCompany?.id
    const userGroupId = selectedCompany?.type === "tenant" ? selectedCompany.id : undefined

    // Reset dependent fields when company changes
    useEffect(() => {
        setFieldValue("user", null)
        setFieldValue("newBookingOwner", null)
        setUserSearchInput("")
        setNewOwnerSearchInput("")
    }, [selectedCompany?.id, selectedCompany?.type === "tenant" ? selectedCompany.customerId : undefined])

    // Reset new booking owner when action changes away from move
    useEffect(() => {
        if (values.action !== ManageBookingsAction.CHANGE_ORGANIZER) {
            setFieldValue("newBookingOwner", null)
            setNewOwnerSearchInput("")
        }
    }, [values.action])

    // Clear new booking owner if user changes to the same person
    useEffect(() => {
        if (values.user && values.newBookingOwner && values.user.id === values.newBookingOwner.id) {
            setFieldValue("newBookingOwner", null)
            setNewOwnerSearchInput("")
        }
    }, [values.user?.id])

    const companiesSelector = useCallback((data: CompaniesResponse) => mapCompaniesToOptions(data), [])

    const companiesResult = useApiQuery(fetchCompanies, {
        queryName: ManageBookingsQuery.COMPANIES,
        dependencies: { serviceCenterId: serviceCenterId ?? 0 },
        enabled: Boolean(serviceCenterId),
        select: companiesSelector,
    })

    const hasUserSearch = userSearchInput.trim().length >= MIN_USER_SEARCH_LENGTH
    const hasNewOwnerSearch = newOwnerSearchInput.trim().length >= MIN_USER_SEARCH_LENGTH

    const usersForCompanyResult = useApiQuery(fetchUsersForCompany, {
        queryName: ManageBookingsQuery.USERS_FOR_COMPANY,
        dependencies: { serviceCenterId: serviceCenterId ?? 0, companyId: companyId ?? 0, userGroupId },
        enabled: Boolean(serviceCenterId) && Boolean(companyId) && (hasUserSearch || hasNewOwnerSearch),
        select: selectUsers,
    })

    const noUsersFoundText = l10n.getString("service-center-admin-organization-manage-bookings-user-no-results")
    const userNoOptionsText = hasUserSearch ? noUsersFoundText : ""
    const newOwnerNoOptionsText = hasNewOwnerSearch ? noUsersFoundText : ""

    const selectPlaceholder = l10n.getString("service-center-admin-organization-manage-bookings-select-placeholder")
    const actionPlaceholder = l10n.getString(
        "service-center-admin-organization-manage-bookings-select-action-placeholder",
    )

    const renderActionValue = (value: unknown): ReactNode => {
        if (value === ManageBookingsAction.CHANGE_ORGANIZER) {
            return l10n.getString("service-center-admin-organization-manage-bookings-action-move")
        }
        if (value === ManageBookingsAction.CANCEL_MEETING) {
            return l10n.getString("service-center-admin-organization-manage-bookings-action-delete")
        }
        return <span className={classes.placeholderText}>{actionPlaceholder}</span>
    }

    const companyOptions = useMemo(() => companiesResult.data ?? [], [companiesResult.data])

    return (
        <Box className={classes.container}>
            <LocalizedStrict
                id="service-center-admin-organization-manage-bookings-select-action"
                attrs={{ label: true }}
            >
                <LabeledField label="Select action" bold={false} className={classes.actionDropdown}>
                    <FormikDropdown<ManageBookingsFormValues>
                        name="action"
                        displayEmpty
                        renderValue={renderActionValue}
                    >
                        <MenuItem value={ManageBookingsAction.CHANGE_ORGANIZER}>
                            <LocalizedStrict id="service-center-admin-organization-manage-bookings-action-move">
                                <span>Move to new owner</span>
                            </LocalizedStrict>
                        </MenuItem>
                        <DestructiveMenuItem value={ManageBookingsAction.CANCEL_MEETING}>
                            <LocalizedStrict id="service-center-admin-organization-manage-bookings-action-delete">
                                <span>Delete</span>
                            </LocalizedStrict>
                        </DestructiveMenuItem>
                    </FormikDropdown>
                </LabeledField>
            </LocalizedStrict>

            <Box className={classes.checkboxSection}>
                {values.action === ManageBookingsAction.CHANGE_ORGANIZER && (
                    <LocalizedStrict id="service-center-admin-organization-manage-bookings-what-to-move-label">
                        <Typography variant="body2" color="text.secondary">
                            What to move:
                        </Typography>
                    </LocalizedStrict>
                )}
                <Box className={classes.checkboxRow}>
                    <LocalizedStrict id="service-center-admin-organization-manage-bookings-checkbox-bookings">
                        <FormikCheckbox<ManageBookingsFormValues> name="updateResourceBooking">
                            <Typography variant="body2" color="text.secondary">
                                Bookings
                            </Typography>
                        </FormikCheckbox>
                    </LocalizedStrict>
                    <LocalizedStrict id="service-center-admin-organization-manage-bookings-checkbox-meeting-service">
                        <FormikCheckbox<ManageBookingsFormValues> name="updateMeetingService">
                            <Typography variant="body2" color="text.secondary">
                                Meeting service
                            </Typography>
                        </FormikCheckbox>
                    </LocalizedStrict>
                </Box>
            </Box>

            {values.action === ManageBookingsAction.CANCEL_MEETING && (
                <LocalizedStrict
                    id="service-center-admin-organization-manage-bookings-alert-delete"
                    attrs={{ title: true }}
                >
                    <StandardAlertInfo
                        title="All future bookings for this user will be canceled."
                        severity="error"
                        useDefaultStyles
                        className={classes.alertBoxFullWidth}
                    />
                </LocalizedStrict>
            )}

            {showMoveFields && (
                <LocalizedStrict id="service-center-admin-organization-manage-bookings-current-owner-title">
                    <Typography variant="body1" className={classes.sectionTitle}>
                        Current owner
                    </Typography>
                </LocalizedStrict>
            )}

            <LocalizedStrict
                id="service-center-admin-organization-manage-bookings-select-company"
                attrs={{ label: true }}
                key="company"
            >
                <LabeledField label="Select company" bold={false} className={classes.companySelect}>
                    <FormikAutoComplete<ManageBookingsFormValues, CompanySelectOption>
                        name="company"
                        label=""
                        fullWidth
                        renderInput={() => <></>}
                        options={companyOptions}
                        groupBy={(option) => option.group}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) =>
                            option.type === value.type &&
                            option.id === value.id &&
                            (option.type === "tenant" && value.type === "tenant"
                                ? option.customerId === value.customerId
                                : true)
                        }
                        renderOption={(props, option) => (
                            <Box component="li" className={classes.companyOption} {...props}>
                                {option.name}
                            </Box>
                        )}
                        InputProps={{ placeholder: selectPlaceholder }}
                    />
                </LabeledField>
            </LocalizedStrict>

            <LocalizedStrict id="service-center-admin-organization-manage-bookings-select-user" attrs={{ label: true }}>
                <LabeledField label="Select user" bold={false} className={classes.userSelect}>
                    <FormikAutoComplete<ManageBookingsFormValues, UserOption>
                        name="user"
                        label=""
                        fullWidth
                        renderInput={() => <></>}
                        options={hasUserSearch ? (usersForCompanyResult.data ?? []) : []}
                        loading={hasUserSearch && usersForCompanyResult.isInitialLoading}
                        noOptionsText={userNoOptionsText}
                        getOptionLabel={(option) => option.displayName}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={!values.company}
                        onInputChange={(_event, newInputValue, reason) => {
                            if (reason === "input") {
                                setUserSearchInput(newInputValue)
                            } else if (reason === "clear") {
                                setUserSearchInput("")
                            }
                        }}
                        InputProps={{ placeholder: selectPlaceholder }}
                    />
                </LabeledField>
            </LocalizedStrict>

            {showMoveFields && (
                <LocalizedStrict id="service-center-admin-organization-manage-bookings-new-owner-title">
                    <Typography variant="body1" className={classes.sectionTitle}>
                        New owner
                    </Typography>
                </LocalizedStrict>
            )}

            {showMoveFields && (
                <LocalizedStrict
                    id="service-center-admin-organization-manage-bookings-alert-move"
                    attrs={{ title: true }}
                >
                    <StandardAlertInfo
                        title="Orders will be moved to a new owner, which you select below."
                        className={classes.alertBox}
                    />
                </LocalizedStrict>
            )}

            {showMoveFields && (
                <LocalizedStrict
                    id="service-center-admin-organization-manage-bookings-select-new-owner"
                    attrs={{ label: true }}
                >
                    <LabeledField label="Select new booking owner" bold={false} className={classes.userSelect}>
                        <FormikAutoComplete<ManageBookingsFormValues, UserOption>
                            name="newBookingOwner"
                            renderInput={() => <></>}
                            label=""
                            fullWidth
                            options={
                                hasNewOwnerSearch
                                    ? (usersForCompanyResult.data ?? []).filter((u) => u.id !== values.user?.id)
                                    : []
                            }
                            loading={hasNewOwnerSearch && usersForCompanyResult.isInitialLoading}
                            noOptionsText={newOwnerNoOptionsText}
                            getOptionLabel={(option) => option.displayName}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onInputChange={(_event, newInputValue, reason) => {
                                if (reason === "input") {
                                    setNewOwnerSearchInput(newInputValue)
                                } else if (reason === "clear") {
                                    setNewOwnerSearchInput("")
                                }
                            }}
                            InputProps={{ placeholder: selectPlaceholder }}
                        />
                    </LabeledField>
                </LocalizedStrict>
            )}
        </Box>
    )
}
